import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Route guard. Three modes (mutually compatible):
 *  1. Basic auth — redirects to /login when not signed in.
 *  2. `roles=[...]` — legacy explicit allow-list (kept for backwards compat).
 *  3. `menuKey="..."` — dynamic per-menu visibility from Settings > Menu Access.
 *     Admin bypasses all restrictions.
 */
const ProtectedRoute = ({ children, roles, menuKey }) => {
  const { user, canAccess } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const roleDenied  = roles && !roles.includes(user.role);
  const menuDenied  = menuKey && !canAccess(menuKey);

  if (roleDenied || menuDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="access-denied">
        <div className="text-center max-w-md">
          <div className="font-heading text-2xl font-bold mb-2">Access Denied</div>
          <p className="text-muted-foreground text-sm">
            You don't have permission to view this page.
            {menuKey && <span className="block mt-1 text-xs">Contact an administrator to request access to <b>{menuKey.replace("_", " ")}</b>.</span>}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
